import { useEquipment } from '@services/common'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ComboboxOption } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'
import { MultiCombobox } from '@/components/ui/multi-combobox'

interface Props {
  value?: string[]
  onChange: (value: string[]) => void
  initial?: { id: string; name: string }[]
  disabled?: boolean
}

export function EquipmentSelect({
  value = [],
  onChange,
  initial = [],
  disabled
}: Props) {
  const { t } = useTranslation('application')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [options, setOptions] = useState<ComboboxOption[]>(
    initial.map((e) => ({ value: e.id, label: e.name }))
  )

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading } = useEquipment({ search: debounced })

  useEffect(() => {
    if (!data) return
    setOptions((prev) => {
      const ids = new Set(prev.map((o) => o.value))
      const fresh = data
        .filter((e) => e.id && !ids.has(e.id))
        .map((e) => ({ value: e.id!, label: e.name ?? '' }))
      return fresh.length ? [...prev, ...fresh] : prev
    })
  }, [data])

  return (
    <div>
      <Label className="mb-1.5 block">{t('form.sections.equipment.label')}</Label>
      <MultiCombobox
        value={value}
        onChange={onChange}
        onSearch={setSearch}
        options={options}
        loading={isLoading}
        disabled={disabled}
        placeholder={t('form.common.selectEquipment')}
      />
    </div>
  )
}
